import { useUsers } from '@src/hooks/useUsers';
import { useContextBlotterFilter } from '@src/providers/ContextBlotterFilterProvider/useContextBlotterFilter';
import {
  FilterClauseType,
  IconName,
  LookbackOption,
  TransferStatusEnum,
  cleanupInitialFilterDateRange,
  convertDateRangeToFilterValue,
  filterExistsAndExcludes,
  useCurrenciesContext,
  useMarketAccountsContext,
  useMarketsContext,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type DateRangeFilter,
  type FilterClause,
  type FilterableProperty,
  type Transfer,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { compact, isEqual, keys, uniq } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';

export interface TransfersBlotterFilter extends DateRangeFilter {
  Provider?: string[];
  Currency?: string[];
  Status?: string[];
  Submitter?: string[];
  Account?: string[];
}

export interface UseTransfersFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
  defaultFilter?: TransfersBlotterFilter;
}

export const DEFAULT_START_DATE_LOOKBACK = LookbackOption.Past30Days;

export function useTransfersFilter<TData extends Transfer>({
  persistedBlotterTable,
  defaultFilter,
}: UseTransfersFilterParams) {
  const { initialState, onFilterChanged: saveFilter } = persistedBlotterTable;
  const [filter, setFilter] = useState<TransfersBlotterFilter>(() =>
    cleanupInitialFilterDateRange(initialState?.filter)
  );
  const { marketAccountsByID } = useMarketAccountsContext();
  const { additionalFilterState } = useContextBlotterFilter();

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const priorFilter = filter;
      let newFilter = action instanceof Function ? action(filter) : action;

      if (!newFilter.StartDate && !newFilter.EndDate) {
        newFilter = convertDateRangeToFilterValue(
          { lookback: defaultFilter?._start || DEFAULT_START_DATE_LOOKBACK },
          newFilter
        );
        setFilter(newFilter);
        saveFilter(newFilter);
      } else if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [filter, saveFilter, defaultFilter]
  );

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<TData>>(
    row => {
      const data = row.data;

      // external MarketAccounts filter applies to the Account column filter
      // without mutating the local filter state object
      const effectiveFilter =
        additionalFilterState && 'MarketAccounts' in additionalFilterState
          ? {
              ...filter,
              Account: additionalFilterState.MarketAccounts,
            }
          : filter;

      if (filterExistsAndExcludes(effectiveFilter, 'Provider', data, 'Market')) {
        return false;
      }
      if (filterExistsAndExcludes(effectiveFilter, 'Currency', data, 'Currency')) {
        return false;
      }
      if (filterExistsAndExcludes(effectiveFilter, 'Status', data, 'Status')) {
        return false;
      }

      if (filterExistsAndExcludes(effectiveFilter, 'Submitter', data, 'User')) {
        return false;
      }

      if (data && effectiveFilter.Account?.length) {
        const fromMarketAccount = marketAccountsByID.get(data.FromMarketAccountID)?.Name ?? '';
        const toMarketAccount = marketAccountsByID.get(data.ToMarketAccountID)?.Name ?? '';
        if (
          !(effectiveFilter.Account.includes(fromMarketAccount) || effectiveFilter.Account.includes(toMarketAccount))
        ) {
          return false;
        }
      }

      return true;
    },
    [additionalFilterState, filter, marketAccountsByID]
  );
  const { marketsByName, marketsList } = useMarketsContext();
  const { currenciesList } = useCurrenciesContext();
  const users = useUsers();
  const { marketAccountsList, marketAccountsByName } = useMarketAccountsContext();

  const filterableProperties = useMemo(() => {
    return compact<FilterableProperty<keyof TransfersBlotterFilter>>([
      {
        key: 'Provider',
        label: 'Provider',
        control: 'multi-select',
        icon: IconName.PresentationChartLine,
        options: marketsList.map(market => market.Name),
        getOptionLabel: (marketName: string) => marketsByName.get(marketName)?.DisplayName ?? marketName,
      },
      {
        key: 'Currency',
        label: 'Currency',
        icon: IconName.CurrencyDollar,
        options: currenciesList.map(cur => cur.Symbol),
        getOptionLabel: (option: string) => option,
      },
      {
        key: 'Status',
        label: 'Status',
        icon: IconName.CheckCircle,
        options: Object.keys(TransferStatusEnum),
        getOptionLabel: status => status,
      },
      {
        key: 'Submitter',
        label: 'Submitter',
        icon: IconName.User,
        options: uniq(users.map(u => u.Name) || []),
        getOptionLabel: (option: string) => option,
      },
      // if MarketAccounts filter is externally set, do not show the Account filter
      // TODO: Adjust this so it's directly compatible to useContextBlotterFilter's
      // filter cleanup function (due to the key difference, that can't be done right now)
      additionalFilterState && 'MarketAccounts' in additionalFilterState
        ? undefined
        : {
            key: 'Account',
            label: 'Account',
            icon: IconName.Identification,
            options: marketAccountsList.map(marketAccount => marketAccount.Name),
            getOptionLabel: (marketName: string) => marketAccountsByName.get(marketName)?.DisplayName ?? marketName,
          },
    ]);
  }, [
    additionalFilterState,
    currenciesList,
    marketAccountsByName,
    marketAccountsList,
    marketsByName,
    marketsList,
    users,
  ]);
  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      (keys(filter) as (keyof TransfersBlotterFilter)[]).forEach((key: keyof TransfersBlotterFilter) => {
        switch (key) {
          case '_start':
          case 'StartDate':
          case 'EndDate':
            return;
          default:
            clauses.push({
              key: key,
              type: FilterClauseType.INCLUSIVE,
              selections: filter[key] as string[],
            });
        }
      });
    }
    return clauses;
  }, [filter]);

  return {
    filter,
    filterableProperties,
    initialFilterClauses,
    changeFilter,
    clientSideFilter,
  };
}
